import React, { useEffect, useRef, useState } from 'react';

import { toast } from 'react-toastify';
/**
 * Mouse / touch signature pad. Call getDataUrl() via ref methods
 * exposed through onReady, or use the Insert button callback.
 */
export default function DrawableSignaturePad({
  onInsert,
  onClear,
  width = 560,
  height = 180,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ratio = window.devicePixelRatio || 1;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.25;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }, [width, height]);

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches?.[0] || event;
    return {
      x: source.clientX - rect.left,
      y: source.clientY - rect.top,
    };
  };

  const startDraw = (event) => {
    event.preventDefault();
    const point = getPoint(event);
    if (!point) return;
    drawingRef.current = true;
    lastPointRef.current = point;
  };

  const draw = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const point = getPoint(event);
    const last = lastPointRef.current;
    if (!ctx || !point || !last) return;

    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    if (!hasInk) setHasInk(true);
  };

  const endDraw = (event) => {
    if (event) event.preventDefault();
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clearPad = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    setHasInk(false);
    onClear?.();
  };

  const insertDrawn = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInk) {
      toast.error('Please draw your signature first.');
      return;
    }
    // Transparent-friendly PNG of ink only would need extra processing;
    // white background is fine for email signatures.
    const dataUrl = canvas.toDataURL('image/png');
    onInsert?.(dataUrl);
  };

  return (
    <div className="email-signature-draw-wrap">
      <div className="email-signature-draw-label">
        Draw your signature below (mouse or touch)
      </div>
      <canvas
        ref={canvasRef}
        className="email-signature-draw-canvas"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <div className="email-signature-draw-actions">
        <button
          type="button"
          className="email-signature-draw-clear"
          onClick={clearPad}
        >
          Clear
        </button>
        <button
          type="button"
          className="email-signature-draw-insert"
          onClick={insertDrawn}
          disabled={!hasInk}
        >
          Use drawn signature
        </button>
      </div>
    </div>
  );
}
